import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  UniversalLogBatchStrategy,
  UniversalLogEntry,
  UniversalLogInput,
  UniversalLogLevel,
  UniversalLogSource,
} from '../types/logging';
import { useApiBase } from './useApiBase';
import { authFetch } from '../lib/authFetch';
import { generateId } from '../lib/id';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../components/SettingsContext';
import { createBugReport } from '../api/bugs';
import { collectBugContext } from '../utils/bugContext';
import { getCurrentDeviceKind } from '../lib/deviceClassification';
import {
  buildAutomaticCaptureExcerpt,
  buildAutomaticBugReportFingerprint,
  buildAutomaticBugReportPayload,
  isIgnoredAutomaticBugMessage,
  markAutomaticBugReportSubmitted,
  shouldAutoReportLogEntry,
  shouldThrottleAutomaticBugReport,
} from '../lib/automaticBugReports';
import {
  DEFAULT_LOGGING_PREFERENCES,
  resolveLoggingPreferences,
  type LoggingPreferences,
} from '../utils/userPreferences';

const DEFAULT_MAX_ENTRIES = 500;
const DEFAULT_CATEGORY = 'general';
const DEFAULT_SOURCE: UniversalLogSource = 'frontend';
const DEFAULT_LEVEL: UniversalLogLevel = 'info';
const MIN_CAPTURE_LEVEL: UniversalLogLevel = 'warn';

const LEVEL_PRIORITY: Record<UniversalLogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function shouldCaptureLevel(level: UniversalLogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[MIN_CAPTURE_LEVEL];
}

type UniversalLoggerState = {
  entries: UniversalLogEntry[];
  maxEntries: number;
};

type UniversalLoggerAction =
  | { type: 'log'; entry: UniversalLogEntry }
  | { type: 'logMany'; entries: UniversalLogEntry[]; strategy: UniversalLogBatchStrategy }
  | { type: 'clear' }
  | { type: 'setMaxEntries'; maxEntries: number };

type UniversalLoggerContextValue = {
  entries: UniversalLogEntry[];
  maxEntries: number;
  loggingPreferences: LoggingPreferences;
  log: (input: UniversalLogInput) => UniversalLogEntry;
  logMany: (inputs: UniversalLogInput[], strategy?: UniversalLogBatchStrategy) => UniversalLogEntry[];
  setMaxEntries: (maxEntries: number) => void;
  clear: () => void;
  logDebug: (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) => UniversalLogEntry;
  logInfo: (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) => UniversalLogEntry;
  logWarn: (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) => UniversalLogEntry;
  logError: (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) => UniversalLogEntry;
  refresh: (options?: { strategy?: UniversalLogBatchStrategy }) => Promise<void>;
  refreshState: {
    loading: boolean;
    error: Error | null;
  };
};

const UniversalLoggerContext = createContext<UniversalLoggerContextValue | undefined>(undefined);

function sortByTimestamp(entries: UniversalLogEntry[]): UniversalLogEntry[] {
  return [...entries].sort((a, b) => b.timestamp - a.timestamp);
}

function enforceLimit(entries: UniversalLogEntry[], maxEntries: number) {
  if (entries.length <= maxEntries) {
    return entries;
  }
  return entries.slice(0, maxEntries);
}

function mergeEntries(
  incoming: UniversalLogEntry[],
  existing: UniversalLogEntry[],
  maxEntries: number
): UniversalLogEntry[] {
  if (!incoming.length) {
    return existing;
  }
  const combined = [...incoming, ...existing];
  const seen = new Set<string>();
  const deduped: UniversalLogEntry[] = [];
  for (const entry of combined) {
    if (seen.has(entry.id)) {
      continue;
    }
    seen.add(entry.id);
    deduped.push(entry);
  }
  return enforceLimit(sortByTimestamp(deduped), maxEntries);
}

function replaceEntries(entries: UniversalLogEntry[], maxEntries: number) {
  return enforceLimit(sortByTimestamp(entries), maxEntries);
}

function loggerReducer(state: UniversalLoggerState, action: UniversalLoggerAction): UniversalLoggerState {
  switch (action.type) {
    case 'log': {
      const next = mergeEntries([action.entry], state.entries, state.maxEntries);
      return { ...state, entries: next };
    }
    case 'logMany': {
      const next =
        action.strategy === 'replace'
          ? replaceEntries(action.entries, state.maxEntries)
          : mergeEntries(action.entries, state.entries, state.maxEntries);
      return { ...state, entries: next };
    }
    case 'clear':
      return { ...state, entries: [] };
    case 'setMaxEntries': {
      const maxEntries = Math.max(1, action.maxEntries);
      return {
        maxEntries,
        entries: enforceLimit(state.entries, maxEntries),
      };
    }
    default:
      return state;
  }
}

const LOG_ID_PREFIX = 'log';

function createLogId(): string {
  return generateId({ prefix: LOG_ID_PREFIX, length: 24 });
}

function safeString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof (value as { message?: string }).message === 'string') {
    return (value as { message?: string }).message;
  }
  try {
    return value ? String(value) : undefined;
  } catch {
    return undefined;
  }
}

function resolveDefaultPage(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const { pathname, search, hash } = window.location;
  return `${pathname}${search || ''}${hash || ''}` || undefined;
}

function toEntry(input: UniversalLogInput): UniversalLogEntry {
  const timestamp =
    typeof input.timestamp === 'number' && Number.isFinite(input.timestamp) ? input.timestamp : Date.now();
  const level = input.level ?? DEFAULT_LEVEL;
  const source = input.source ?? DEFAULT_SOURCE;
  const category = input.category ?? DEFAULT_CATEGORY;
  const page = input.page ?? resolveDefaultPage();
  return {
    id: input.id || createLogId(),
    timestamp,
    level,
    source,
    category,
    page,
    message: input.message,
    details: input.details,
    stackTrace: input.stackTrace,
    userId: input.userId,
    sessionId: input.sessionId,
  };
}

function logWithLevel(
  log: (input: UniversalLogInput) => UniversalLogEntry,
  level: UniversalLogLevel,
  message: string,
  input?: Omit<UniversalLogInput, 'message' | 'level'>
) {
  return log({
    ...input,
    level,
    message,
  });
}

export function UniversalLoggerProvider({
  children,
  maxEntries = DEFAULT_MAX_ENTRIES,
  initialEntries = [],
  onRefreshError,
}: {
  children: ReactNode;
  maxEntries?: number;
  initialEntries?: UniversalLogInput[];
  onRefreshError?: (error: Error) => void;
}) {
  const preparedInitialState: UniversalLoggerState = {
    entries: initialEntries.length
      ? replaceEntries(initialEntries.map(toEntry), Math.max(1, maxEntries))
      : [],
    maxEntries: Math.max(1, maxEntries),
  };

  const [state, dispatch] = useReducer(loggerReducer, preparedInitialState);
  const [refreshState, setRefreshState] = useState<{ loading: boolean; error: Error | null }>({
    loading: false,
    error: null,
  });
  const apiBase = useApiBase();
  const mountedRef = useRef(true);
  const { user } = useAuth();
  const { settings } = useSettings();
  const entriesRef = useRef(state.entries);
  const autoBugReportInFlightRef = useRef<Set<string>>(new Set());

  const loggingPreferences = useMemo(() => {
    if (!user || !user.custom_fields || typeof user.custom_fields !== 'object') {
      return { ...DEFAULT_LOGGING_PREFERENCES };
    }
    const customFields = user.custom_fields as Record<string, unknown>;
    const preferencesRaw = customFields.preferences;
    return resolveLoggingPreferences(preferencesRaw);
  }, [user]);

  const loggingEnabledRef = useRef(loggingPreferences.enableUniversalLogging);
  const captureWindowErrorsRef = useRef(loggingPreferences.captureWindowErrors);
  const capturePromiseRejectionsRef = useRef(loggingPreferences.capturePromiseRejections);
  useEffect(() => {
    loggingEnabledRef.current = loggingPreferences.enableUniversalLogging;
  }, [loggingPreferences.enableUniversalLogging]);
  useEffect(() => {
    captureWindowErrorsRef.current = loggingPreferences.captureWindowErrors;
  }, [loggingPreferences.captureWindowErrors]);
  useEffect(() => {
    capturePromiseRejectionsRef.current = loggingPreferences.capturePromiseRejections;
  }, [loggingPreferences.capturePromiseRejections]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    entriesRef.current = state.entries;
  }, [state.entries]);

  const setMaxEntries = useCallback((nextMaxEntries: number) => {
    dispatch({ type: 'setMaxEntries', maxEntries: nextMaxEntries });
  }, []);

  const maybeSubmitAutomaticBugReport = useCallback(
    (entry: UniversalLogEntry) => {
      if (typeof window === 'undefined') {
        return;
      }

      const deviceKind = getCurrentDeviceKind();
      if (!shouldAutoReportLogEntry(entry, { deviceKind })) {
        return;
      }

      const fingerprint = `rh-tools:auto-bug:${buildAutomaticBugReportFingerprint(entry)}`;
      if (autoBugReportInFlightRef.current.has(fingerprint)) {
        return;
      }

      const now = Date.now();
      if (shouldThrottleAutomaticBugReport(window.localStorage, fingerprint, now)) {
        return;
      }

      autoBugReportInFlightRef.current.add(fingerprint);

      const context = collectBugContext({
        settings,
        user,
        entries: [entry, ...entriesRef.current].slice(0, 20),
        extra: {
          automatic_capture: {
            fingerprint,
            ...buildAutomaticCaptureExcerpt(entry),
          },
        },
      });

      const reporterName = user?.display_name || user?.username || user?.id || 'Automatic mobile capture';

      void createBugReport(apiBase, buildAutomaticBugReportPayload({ entry, context, reporterName }))
        .then(() => {
          markAutomaticBugReportSubmitted(window.localStorage, fingerprint, now);
        })
        .catch(() => {
          // Ignore reporting failures to avoid recursive log spam on broken clients.
        })
        .finally(() => {
          autoBugReportInFlightRef.current.delete(fingerprint);
        });
    },
    [apiBase, settings, user]
  );

  const log = useCallback((input: UniversalLogInput) => {
    const entry = toEntry(input);
    if (loggingEnabledRef.current && shouldCaptureLevel(entry.level)) {
      dispatch({ type: 'log', entry });
    }
    maybeSubmitAutomaticBugReport(entry);
    return entry;
  }, [maybeSubmitAutomaticBugReport]);

  const logMany = useCallback(
    (inputs: UniversalLogInput[], strategy: UniversalLogBatchStrategy = 'append') => {
      const entries = inputs.map(toEntry);
      if (loggingEnabledRef.current) {
        dispatch({ type: 'logMany', entries, strategy });
      }
      return entries;
    },
    []
  );

  const clear = useCallback(() => {
    dispatch({ type: 'clear' });
  }, []);

  const logDebug = useCallback(
    (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) =>
      logWithLevel(log, 'debug', message, input),
    [log]
  );

  const logInfo = useCallback(
    (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) =>
      logWithLevel(log, 'info', message, input),
    [log]
  );

  const logWarn = useCallback(
    (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) =>
      logWithLevel(log, 'warn', message, input),
    [log]
  );

  const logError = useCallback(
    (message: string, input?: Omit<UniversalLogInput, 'message' | 'level'>) =>
      logWithLevel(log, 'error', message, input),
    [log]
  );

  const refresh = useCallback(
    async (options?: { strategy?: UniversalLogBatchStrategy }) => {
      const strategy = options?.strategy ?? 'replace';
      const normalizedBase = apiBase ? apiBase.replace(/\/+$/, '') : '';
      const url = `${normalizedBase}/api/dev/logs/universal?limit=${state.maxEntries}`;

      setRefreshState({ loading: true, error: null });
      try {
        const response = await authFetch(url);
        const payload = await response.json().catch(() => null);
        if (!mountedRef.current) {
          setRefreshState({ loading: false, error: null });
          return;
        }
        if (!response.ok || !payload || !Array.isArray(payload.logs)) {
          const error = new Error(
            `Refresh failed: ${response.ok ? 'Invalid response format' : `${response.status} ${response.statusText}`}`
          );
          setRefreshState({ loading: false, error });
          if (onRefreshError) {
            onRefreshError(error);
          }
          return;
        }
        const entries: UniversalLogInput[] = (payload.logs as UniversalLogEntry[]).map((entry) => ({
          ...entry,
          id: entry.id,
          timestamp: entry.timestamp,
          level: entry.level,
          source: entry.source,
          category: entry.category,
          page: entry.page,
          message: entry.message,
          details: entry.details,
          stackTrace: entry.stackTrace,
          userId: entry.userId,
          sessionId: entry.sessionId,
        }));
        logMany(entries, strategy);
        setRefreshState({ loading: false, error: null });
      } catch (error) {
        if (!mountedRef.current) {
          setRefreshState({ loading: false, error: null });
          return;
        }
        const refreshError = error instanceof Error ? error : new Error('Unknown refresh error');
        setRefreshState({ loading: false, error: refreshError });
        if (onRefreshError) {
          onRefreshError(refreshError);
        }
      }
    },
    [apiBase, logMany, state.maxEntries, onRefreshError]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onWindowError = (event: ErrorEvent) => {
      if (!captureWindowErrorsRef.current) {
        return;
      }

      const message = event.message || safeString(event.error);
      if (!message || isIgnoredAutomaticBugMessage(message, event.error instanceof Error ? event.error.stack : undefined)) {
        return;
      }

      log({
        level: 'error',
        source: 'frontend',
        category: 'window/error',
        page: resolveDefaultPage(),
        message,
        details: {
          filename: event.filename || undefined,
          line: event.lineno || undefined,
          column: event.colno || undefined,
          name: event.error instanceof Error ? event.error.name : undefined,
          stack: event.error instanceof Error ? event.error.stack : undefined,
        },
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!capturePromiseRejectionsRef.current) {
        return;
      }

      const message = safeString(event.reason);
      if (!message || isIgnoredAutomaticBugMessage(message, event.reason instanceof Error ? event.reason.stack : undefined)) {
        return;
      }

      log({
        level: 'error',
        source: 'frontend',
        category: 'window/unhandledrejection',
        page: resolveDefaultPage(),
        message,
        details: {
          reason: message,
          stack: event.reason instanceof Error ? event.reason.stack : undefined,
        },
      });
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [log]);

  // Auto-refresh removed: logs load only when refresh() is invoked explicitly.

  const value = useMemo<UniversalLoggerContextValue>(
    () => ({
      entries: state.entries,
      maxEntries: state.maxEntries,
      loggingPreferences,
      log,
      logMany,
      setMaxEntries,
      clear,
      logDebug,
      logInfo,
      logWarn,
      logError,
      refresh,
      refreshState,
    }),
    [
      state.entries,
      state.maxEntries,
      loggingPreferences,
      log,
      logMany,
      setMaxEntries,
      clear,
      logDebug,
      logInfo,
      logWarn,
      logError,
      refresh,
      refreshState,
    ]
  );

  return React.createElement(UniversalLoggerContext.Provider, { value }, children);
}

export function useUniversalLogger() {
  const context = useContext(UniversalLoggerContext);
  if (!context) throw new Error('useUniversalLogger must be used within a UniversalLoggerProvider');
  return context;
}

export function useUniversalLogs() {
  const { entries } = useUniversalLogger();
  return entries;
}
